import type { AuthResult } from '@/types/Auth';
import { logger } from '@equip/utils/logger';
import { telemetry } from '@/utils/telemetry';

// Auth service configuration constants
const AUTH_TIMEOUT_MS = 10000; // 10 seconds - timeout for token exchange/refresh requests
const MAX_RETRIES = 2; // Maximum retry attempts for failed auth requests
const CODE_EXPIRY_MS = 60 * 1000; // OAuth 2.0 spec: authorization codes expire after 60 seconds
const MAX_STORED_CODES = 50; // Maximum number of used authorization codes to track (prevents unbounded growth)
const DEFAULT_WEB_ORIGIN = 'http://localhost:3002';
const getWindowOrigin = (): string =>
  typeof window !== 'undefined' && typeof window.location?.origin === 'string'
    ? window.location.origin
    : DEFAULT_WEB_ORIGIN;

/**
 * Authentication service for OAuth2/OIDC flows with Keycloak.
 * Implements Authorization Code + PKCE flow for secure SPA authentication.
 */
export class AuthService {
  // When VITE_KEYCLOAK_URL is not set the Keycloak realm is served from the
  // same origin as the web app (e.g. https://fqdn/realms/equip in production,
  // http://localhost:3002/realms/equip in dev via Kong port-forward).
  // No port number is hardcoded here.
  private keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL || `${getWindowOrigin()}/realms/equip`;
  private clientId = 'equip-web-ui'; // Public client - no secret required with PKCE
  // Auto-redirect to Azure Workforce is enabled by default for all environments.
  // Set VITE_ENABLE_AUTO_AZURE_WORKFORCE=false to show the Keycloak login page instead.
  private enableAutoAzureWorkforce = import.meta.env.VITE_ENABLE_AUTO_AZURE_WORKFORCE !== 'false';

  private createNonRetryableError(message: string): Error {
    const error = new Error(message);
    error.name = 'NonRetryableAuthError';
    return error;
  }

  private isNonRetryableError(error: unknown): error is Error {
    return error instanceof Error && error.name === 'NonRetryableAuthError';
  }

  /**
   * Initiates OAuth2 login flow with PKCE.
   * Redirects user to Keycloak, which may further redirect to Azure Workforce if configured.
   * @param silent If true, uses prompt=none for silent SSO authentication (no user interaction)
   */
  async login(silent = false): Promise<void> {
    const startTime = Date.now();
    telemetry.track('auth.login.started', { silent });

    // OAuth2 Authorization Code + PKCE Flow (secure for SPAs)
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    const state = this.generateState();

    // Store PKCE verifier temporarily (session only)
    sessionStorage.setItem('pkce_verifier', codeVerifier);
    sessionStorage.setItem('login_initiated', 'true'); // Track that we initiated login
    sessionStorage.setItem('oauth_state', state);

    if (silent) {
      // Mark this as a silent login attempt
      sessionStorage.setItem('silent_login', 'true');
    }

    // Preserve intended route for post-login redirect
    const intendedRoute = window.location.pathname + window.location.search;
    if (intendedRoute !== '/login') {
      sessionStorage.setItem('intended_route', intendedRoute);
    }

    const authUrl = `${this.keycloakUrl}/protocol/openid-connect/auth`;
    const params = new URLSearchParams({
      client_id: this.clientId,
      response_type: 'code',
      scope: 'openid profile email',
      redirect_uri: window.location.origin + '/auth/callback',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      state,
    });

    // Only auto-redirect to Azure Workforce if explicitly enabled
    // This preserves the ability to use local Keycloak login as fallback
    if (this.enableAutoAzureWorkforce) {
      params.append('kc_idp_hint', 'azure-workforce');
    }

    // Silent authentication: prompt=none will return immediately with code if SSO session exists
    // Otherwise returns error without showing login UI
    if (silent) {
      params.append('prompt', 'none');
      // Force Keycloak to re-authenticate via the IDP instead of reusing its
      // cached SSO session.  Without this, Keycloak issues tokens from session
      // data and never re-brokers to Azure Workforce, so IDP mapper updates (role changes,
      // profile changes) are never picked up.
      params.append('max_age', '0');
    } else {
      // Interactive login: force the Microsoft account chooser so the user can
      // pick (or switch) identities instead of silently reusing the existing
      // Entra session.  Keycloak forwards this to Azure Workforce via the IDP's
      // forwardParameters=prompt config.
      params.append('prompt', 'select_account');
    }

    telemetry.trackDuration('auth.login.redirect', startTime, { silent });
    window.location.href = `${authUrl}?${params}`;
  }

  /**
   * Clears the Keycloak SSO session by redirecting to the OIDC end-session
   * endpoint. The Keycloak session cookies (KEYCLOAK_IDENTITY, AUTH_SESSION_ID)
   * are HttpOnly, so they cannot be removed from JavaScript — a redirect
   * through the logout endpoint is the only reliable way to clear them.
   *
   * Used to recover from a stale/broken SSO session (e.g. a leftover cookie
   * that causes token exchange to fail repeatedly). After logout, Keycloak
   * redirects back to {origin}/login for a clean sign-in.
   */
  clearKeycloakSession(): void {
    // Remove any local auth artifacts before leaving the page.
    this.cleanupAuthSession();

    // Suppress the automatic silent SSO that runs on the next app load.
    // Without this, redirecting back to /login immediately triggers
    // silentReauth() (prompt=none). Because only the Keycloak session was
    // ended — the upstream Azure Workforce (Entra) session is still live —
    // Keycloak would silently re-broker and recreate the very session cookies
    // we are trying to clear. The cooldown flag makes initializeAuth skip
    // silent SSO; interactive "Sign In" is unaffected and a successful login
    // clears the flag. (initializeAuth reads this from localStorage.)
    try {
      localStorage.setItem('silent_sso_last_fail', Date.now().toString());
    } catch {
      // Ignore storage access errors.
    }

    const logoutUrl = `${this.keycloakUrl}/protocol/openid-connect/logout`;
    const params = new URLSearchParams({
      client_id: this.clientId,
      post_logout_redirect_uri: `${getWindowOrigin()}/login`,
    });

    telemetry.track('auth.keycloak_session.cleared');
    window.location.href = `${logoutUrl}?${params}`;
  }

  /**
   * Exchanges authorization code for tokens.
   * Validates PKCE flow and OAuth state to prevent CSRF attacks.
   */
  async exchangeCodeForToken(code: string, returnedState: string | null): Promise<AuthResult> {
    const startTime = Date.now();
    telemetry.track('auth.token_exchange.started');

    // Validate OAuth parameters (prevent injection attacks)
    // Allow alphanumeric, hyphens, underscores, and dots (Keycloak uses dots in authorization codes)
    if (!code || typeof code !== 'string' || !/^[a-zA-Z0-9._-]+$/.test(code)) {
      this.cleanupAuthSession();
      throw new Error('Invalid authorization code format');
    }

    if (!returnedState || typeof returnedState !== 'string' || !/^[a-f0-9]+$/.test(returnedState)) {
      this.cleanupAuthSession();
      throw new Error('Invalid OAuth state format');
    }

    // Check if login was actually initiated
    const loginInitiated = sessionStorage.getItem('login_initiated');
    if (!loginInitiated) {
      this.cleanupAuthSession();
      throw new Error('No login flow detected');
    }

    const codeVerifier = sessionStorage.getItem('pkce_verifier');
    if (!codeVerifier) {
      this.cleanupAuthSession();
      throw new Error('PKCE verifier not found');
    }

    const storedState = sessionStorage.getItem('oauth_state');
    // Note: Standard string comparison is safe here. OAuth state is cryptographically random
    // and single-use, providing CSRF protection. Timing attacks are theoretical in browser contexts.
    if (!storedState || storedState !== returnedState) {
      this.cleanupAuthSession();
      throw new Error('OAuth state mismatch - possible CSRF attack');
    }

    // Prevent double execution with timestamp-based deduplication
    // NOTE: Single-tab only - sessionStorage is not shared across tabs.
    // Multi-tab support would require localStorage or BroadcastChannel API.
    // For now, each tab maintains its own used codes list.
    const codeFingerprint = await this.hashCode(code);
    let usedCodes: Record<string, number> = {};

    try {
      const usedCodesStr = sessionStorage.getItem('used_codes') || '{}';
      usedCodes = JSON.parse(usedCodesStr) as Record<string, number>;
    } catch (_error) {
      // If storage is corrupted, start fresh
      logger.warn('Failed to parse used codes, resetting', { component: 'AuthService' });
      usedCodes = {};
    }

    const now = Date.now();

    // Clean up old entries (older than 60 seconds per OAuth 2.0 spec)
    Object.keys(usedCodes).forEach((key) => {
      const timestamp = usedCodes[key];
      if (timestamp && now - timestamp > CODE_EXPIRY_MS) {
        delete usedCodes[key];
      }
    });

    // Enforce size limit with FIFO eviction
    const codeEntries = Object.entries(usedCodes);
    if (codeEntries.length >= MAX_STORED_CODES) {
      const sorted = codeEntries.sort((a, b) => a[1] - b[1]);
      const toDelete = sorted.slice(0, sorted.length - MAX_STORED_CODES + 1);
      toDelete.forEach(([key]) => delete usedCodes[key]);
    }

    if (usedCodes[codeFingerprint]) {
      throw new Error('Authorization code already used');
    }

    // Mark this code as used with timestamp
    usedCodes[codeFingerprint] = now;
    sessionStorage.setItem('used_codes', JSON.stringify(usedCodes));

    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      const controller = new AbortController();
      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      try {
        timeoutId = setTimeout(() => controller.abort(), 10000);

        const response = await fetch(`${this.keycloakUrl}/protocol/openid-connect/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: this.clientId,
            code,
            redirect_uri: window.location.origin + '/auth/callback',
            code_verifier: codeVerifier,
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          const errorText = await response.text();
          const errorCode = `AUTH_TOKEN_${response.status}`;
          logger.error('Token exchange failed', {
            component: 'AuthService',
            status: response.status,
            errorCode,
            details: errorText.substring(0, 200),
          });

          // Don't retry on client errors (4xx) — authorization codes are single-use;
          // retrying after a 4xx would just burn the same consumed/expired code.
          if (response.status >= 400 && response.status < 500) {
            this.cleanupAuthSession();
            throw this.createNonRetryableError(
              `${this.getUserFriendlyError(response.status, errorText)} (Error code: ${errorCode})`,
            );
          }

          // Retry on server errors (5xx)
          if (attempt < MAX_RETRIES) {
            lastError = new Error(`Server error ${response.status}, retrying...`);
            await this.sleepWithJitter((attempt + 1) * 1000); // Exponential backoff with jitter
            continue;
          }

          throw new Error(`Token exchange failed: ${response.status}`);
        }

        const tokens = await response.json();

        // Validate token structure
        if (!tokens.access_token || !tokens.id_token || !tokens.refresh_token) {
          this.cleanupAuthSession();
          logger.error('Invalid token response structure', {
            component: 'AuthService',
            hasAccessToken: !!tokens.access_token,
            hasIdToken: !!tokens.id_token,
            hasRefreshToken: !!tokens.refresh_token,
          });
          throw new Error('Invalid token response: missing required tokens');
        }

        // CRITICAL: Validate JWT signatures to prevent token forgery
        const { validateToken } = await import('@/utils/jwtUtils');
        await validateToken(tokens.access_token);
        await validateToken(tokens.id_token);

        // Clean up auth session artifacts after successful token exchange
        this.cleanupAuthSession();
        logger.debug('Token exchange successful', { component: 'AuthService' });
        telemetry.trackDuration('auth.token_exchange.success', startTime);
        return tokens;
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          logger.warn('Token exchange timeout', {
            component: 'AuthService',
            attempt: attempt + 1,
            maxRetries: MAX_RETRIES,
          });
          // Authorization codes are single-use: a timeout means the server may have
          // received and processed the request before timing out, so do not retry.
          this.cleanupAuthSession();
          throw this.createNonRetryableError(
            'Token exchange timed out. Please try logging in again.',
          );
        }

        // Don't retry on non-retryable errors (4xx, validation failures)
        if (this.isNonRetryableError(error)) {
          throw error;
        }

        // Don't retry on validation errors
        if (error instanceof Error && error.message.includes('Authentication failed')) {
          throw error;
        }

        // Authorization codes are single-use: any network-level failure (TypeError from
        // CORS-blocked responses, connection reset, etc.) may have been processed server-side
        // even though the browser could not read the response. Do not retry.
        if (error instanceof TypeError) {
          this.cleanupAuthSession();
          logger.warn('Token exchange network error — not retrying to protect single-use code', {
            component: 'AuthService',
            error: String(error),
          });
          throw this.createNonRetryableError(
            'Authentication service unreachable. Please try logging in again.',
          );
        }

        lastError = error as Error;
        if (attempt === MAX_RETRIES) {
          this.cleanupAuthSession();
          logger.error('Token exchange failed after retries', {
            component: 'AuthService',
            error: String(error),
          });
          telemetry.trackError('auth.token_exchange.failed', error, { attempts: MAX_RETRIES + 1 });
          throw error;
        }
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      }
    }

    throw lastError || new Error('Token exchange failed');
  }

  private getUserFriendlyError(status: number, details: string): string {
    if (status === 401) {
      return 'Invalid credentials or session expired. Please try logging in again.';
    }
    if (status === 403) {
      return 'Access denied. Contact your administrator if you believe this is an error.';
    }
    if (status === 400) {
      if (details.includes('invalid_grant')) {
        return 'Authorization code expired or invalid. Please try logging in again.';
      }
      if (details.includes('invalid_request')) {
        return 'Invalid authentication request. Please clear your browser cache and try again.';
      }
    }
    if (status >= 500) {
      return 'Authentication service temporarily unavailable. Please try again in a moment.';
    }
    return 'Authentication failed. Please try again or contact support if the problem persists.';
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private sleepWithJitter(baseMs: number): Promise<void> {
    // Add random jitter (0-50% of base delay) to prevent thundering herd
    const jitter = Math.random() * baseMs * 0.5;
    return this.sleep(baseMs + jitter);
  }

  private async hashCode(code: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(code);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    // Use full 64-character hash for maximum collision resistance (256 bits)
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Refreshes access token using refresh token.
   * Should be called before access token expires to maintain session.
   */
  async refreshToken(refreshToken: string): Promise<AuthResult> {
    const startTime = Date.now();
    telemetry.track('auth.token_refresh.started');

    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      const controller = new AbortController();
      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      try {
        timeoutId = setTimeout(() => controller.abort(), AUTH_TIMEOUT_MS);

        const response = await fetch(`${this.keycloakUrl}/protocol/openid-connect/token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          // Note: No credentials needed - public client with refresh token in body
          body: new URLSearchParams({
            grant_type: 'refresh_token',
            client_id: this.clientId,
            refresh_token: refreshToken,
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          const errorText = await response.text();
          logger.error('Token refresh failed', {
            component: 'AuthService',
            status: response.status,
            details: errorText.substring(0, 200),
          });

          // Retry on server errors (5xx) only
          if (response.status >= 500 && attempt < MAX_RETRIES) {
            lastError = new Error(`Server error ${response.status}, retrying...`);
            await this.sleepWithJitter((attempt + 1) * 1000);
            continue;
          }

          this.cleanupAuthSession();
          if (response.status === 401 || response.status === 400) {
            throw this.createNonRetryableError('Session expired. Please sign in again.');
          }
          throw this.createNonRetryableError(`Token refresh failed: ${response.status}`);
        }

        const tokens = await response.json();

        // Validate token structure
        if (!tokens.access_token || !tokens.refresh_token) {
          logger.error('Invalid refresh token response', {
            component: 'AuthService',
            hasAccessToken: !!tokens.access_token,
            hasRefreshToken: !!tokens.refresh_token,
          });
          this.cleanupAuthSession();
          throw this.createNonRetryableError('Invalid refresh response: missing required tokens');
        }

        // CRITICAL: Validate JWT signatures to prevent token forgery
        const { validateToken } = await import('@/utils/jwtUtils');
        await validateToken(tokens.access_token);
        if (tokens.id_token) {
          await validateToken(tokens.id_token);
        }

        logger.debug('Token refresh successful', { component: 'AuthService' });
        telemetry.trackDuration('auth.token_refresh.success', startTime);
        return tokens;
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          if (attempt < MAX_RETRIES) {
            lastError = new Error('Request timeout, retrying...');
            await this.sleepWithJitter((attempt + 1) * 1000);
            continue;
          }
          this.cleanupAuthSession();
          throw new Error('Token refresh timed out after multiple attempts');
        }

        // Don't retry on CORS/network errors (TypeError from fetch)
        if (error instanceof TypeError) {
          logger.error('Token refresh network/CORS error', {
            component: 'AuthService',
            error: error.message,
          });
          this.cleanupAuthSession();
          throw new Error('Network error during token refresh. Please check your connection.');
        }

        if (this.isNonRetryableError(error)) {
          throw error;
        }

        lastError = error as Error;
        if (attempt === MAX_RETRIES) {
          telemetry.trackError('auth.token_refresh.failed', error, { attempts: MAX_RETRIES + 1 });
          throw error;
        }
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      }
    }

    throw lastError || new Error('Token refresh failed');
  }

  /**
   * Performs a background OIDC silent re-authentication via a hidden iframe.
   *
   * Uses `prompt=none` so no user interaction is required.  The iframe loads
   * `/auth/callback`, detects it is inside an iframe, and relays the
   * authorisation code back to this context via `postMessage`.  The token
   * exchange then runs here in the parent context using the PKCE verifier that
   * was captured in the closure — no `sessionStorage` reads are needed, so
   * an in-flight interactive login flow is never clobbered.
   *
   * Returns fresh tokens when Keycloak's SSO session is still valid, or `null`
   * when it has expired / when Keycloak signals `login_required`.
   */
  async silentReauth(timeoutMs = 30_000): Promise<AuthResult | null> {
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    const state = this.generateState();

    const authUrl = `${this.keycloakUrl}/protocol/openid-connect/auth`;
    const params = new URLSearchParams({
      client_id: this.clientId,
      response_type: 'code',
      scope: 'openid profile email',
      redirect_uri: `${window.location.origin}/auth/callback`,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      state,
      prompt: 'none',
      // Force re-authentication via IDP — without this, Keycloak reuses its
      // cached SSO session and never re-brokers to Azure Workforce.
      max_age: '0',
    });

    if (this.enableAutoAzureWorkforce) {
      params.append('kc_idp_hint', 'azure-workforce');
    }

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    Object.assign(iframe.style, {
      display: 'none',
      width: '0',
      height: '0',
      border: 'none',
      position: 'absolute',
      left: '-9999px',
    });
    document.body.appendChild(iframe);
    iframe.src = `${authUrl}?${params}`;

    return new Promise<AuthResult | null>((resolve) => {
      let settled = false;

      const settle = (result: AuthResult | null): void => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        window.removeEventListener('message', messageHandler);
        try {
          document.body.removeChild(iframe);
        } catch {
          /* already removed */
        }
        resolve(result);
      };

      const timeoutId = setTimeout(() => {
        logger.warn('Silent reauth timed out', { component: 'AuthService' });
        settle(null);
      }, timeoutMs);

      const messageHandler = (event: MessageEvent): void => {
        // Guard against cross-origin message injection
        if (event.origin !== window.location.origin) return;

        const data = event.data as {
          type?: string;
          code?: string;
          state?: string;
          error?: string;
        };
        if (
          !data ||
          (data.type !== 'silent-auth-code' && data.type !== 'silent-auth-error')
        ) {
          return;
        }

        if (data.type === 'silent-auth-error' || !data.code) {
          logger.debug('Silent reauth: SSO session unavailable', {
            component: 'AuthService',
            error: data.error,
          });
          settle(null);
          return;
        }

        // Validate state to prevent CSRF
        if (!data.state || data.state !== state) {
          logger.error('Silent reauth: OAuth state mismatch — possible CSRF attack', {
            component: 'AuthService',
          });
          settle(null);
          return;
        }

        // Exchange code using the in-closure PKCE verifier (no sessionStorage reads)
        const code = data.code;
        const controller = new AbortController();
        const tid = setTimeout(() => controller.abort(), 10_000);

        fetch(`${this.keycloakUrl}/protocol/openid-connect/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: this.clientId,
            code,
            redirect_uri: `${window.location.origin}/auth/callback`,
            code_verifier: codeVerifier,
          }),
          signal: controller.signal,
        })
          .then(async (response) => {
            clearTimeout(tid);
            if (!response.ok) {
              logger.warn('Silent reauth: token exchange failed', {
                component: 'AuthService',
                status: response.status,
              });
              settle(null);
              return;
            }

            const tokens = (await response.json()) as AuthResult;
            if (!tokens.access_token || !tokens.refresh_token || !tokens.id_token) {
              settle(null);
              return;
            }

            const { validateToken } = await import('@/utils/jwtUtils');
            await validateToken(tokens.access_token);
            await validateToken(tokens.id_token);
            logger.debug('Silent reauth successful', { component: 'AuthService' });
            settle(tokens);
          })
          .catch((err: unknown) => {
            clearTimeout(tid);
            logger.warn('Silent reauth: exchange error', {
              component: 'AuthService',
              error: String(err),
            });
            settle(null);
          });
      };

      window.addEventListener('message', messageHandler);
    });
  }

  private cleanupAuthSession(): void {
    // Clean up all auth-related session storage keys
    sessionStorage.removeItem('pkce_verifier');
    sessionStorage.removeItem('login_initiated');
    sessionStorage.removeItem('used_codes');
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('intended_route');
    sessionStorage.removeItem('silent_login');
    sessionStorage.removeItem('auth_init_timestamp');
    sessionStorage.removeItem('silent_sso_last_fail');
    sessionStorage.removeItem('token_exchange_in_progress');
  }

  private generateCodeVerifier(): string {
    // Generate 64-byte verifier for RFC 7636 compliance (produces ~86 character base64url string)
    // RFC 7636 requires 43-128 characters. Using 64 bytes provides better security margin.
    const array = new Uint8Array(64);
    crypto.getRandomValues(array);
    // Convert to base64url without String.fromCharCode.apply (avoids call stack issues)
    let base64 = '';
    for (let i = 0; i < array.length; i++) {
      base64 += String.fromCharCode(array[i]!); // Non-null assertion: array index is always valid
    }
    return btoa(base64).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  private generateState(length = 32): string {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(digest))))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }
}

export const authService = new AuthService();
